import { Answer, DecisionStep, DecisionTreePath } from '../../../types';
import { ASCII_LOWERCASE_A } from './constants';
import { UserStepAnswer } from './types';

export const getOptionLabel = (index: number): string => {
  return String.fromCharCode(ASCII_LOWERCASE_A + index);
};

export const getOptionLabelDecision = (index: number): string => {
  return 'Diálogo ' + Number(index + 1);
};

export const findCorrectAnswer = (answers: Answer[]): Answer | undefined => {
  return answers.find((answer) => answer.correct === 1);
};

export const isAnswerCorrect = (answers: Answer[], selectedAnswerId: number): boolean => {
  const correctAnswer = findCorrectAnswer(answers);
  return correctAnswer?.answer_id === selectedAnswerId;
};

export const createUserStepAnswer = (
  stepIndex: number,
  stepType: 'multiple' | 'single',
  questionText: string,
  selectedAnswerId: number,
  isCorrect: boolean,
  allAnswers: Answer[]
): UserStepAnswer => ({
  stepIndex,
  stepType,
  questionText,
  selectedAnswerId,
  isCorrect,
  allAnswers,
});

export const getOptionCardClassName = (
  isSelected: boolean,
  isCorrectOption: boolean,
  isCorrectAnswer: boolean
): string => {
  if (isSelected && isCorrectOption) return 'is-correct';
  if (isSelected && !isCorrectOption) return 'is-incorrect';
  if (isCorrectAnswer) return 'is-correct-answer';
  return '';
};

export const getStepBadgeClassName = (isCorrect: boolean): string => {
  return isCorrect ? 'is-correct' : 'is-incorrect';
};

/**
 * Finds the decision path for a given step and selected answer.
 * @param decisionTree - Array of all possible decision paths.
 * @param currentStepId - ID of the current step (origin).
 * @param selectedAnswerId - ID of the selected answer.
 * @returns The matching decision path, or `undefined` if no path matches.
 *          When the matched path has `target_step_id === null`, it represents
 *          an explicit finalization path (the tree ends after this answer).
 */
export const findDecisionPath = (
  decisionTree: DecisionTreePath[],
  currentStepId: number,
  selectedAnswerId: number
): DecisionTreePath | undefined => {
  return decisionTree.find(
    (path) => path.origin_step_id === currentStepId && path.answer_id === selectedAnswerId
  );
};

/**
 * Returns the next step id resolved from the decision tree, or `null` when
 * no path matches or when the matched path explicitly finalizes the flow.
 * Kept as a thin wrapper around `findDecisionPath` for backwards compatibility.
 */
export const findNextStepId = (
  decisionTree: DecisionTreePath[],
  currentStepId: number,
  selectedAnswerId: number
): number | null => {
  const path = findDecisionPath(decisionTree, currentStepId, selectedAnswerId);
  return path?.target_step_id ?? null;
};

/**
 * Checks whether a decision path represents an explicit finalization, i.e.
 * the path exists and its `target_step_id` is `null`. This is distinct from
 * "no path found", which keeps the legacy sequential/single-phase behavior.
 */
export const isExplicitFinalization = (path: DecisionTreePath | undefined): boolean => {
  return !!path && path.target_step_id === null;
};

export const findStepIndexById = (steps: DecisionStep[], stepId: number): number => {
  return steps.findIndex((step) => step.id === stepId);
};
